/**
 * stackTransitionPlugin — iOS 26 Spatial Layering for Stackflow.
 *
 * Animation logic delegated to useSpatialPush hook (shared with SpatialOverlay).
 * All constants from spatialTransition.ts (single source of truth).
 */

import { type ReactNode, useRef } from 'react';
import { useActions, type StackflowReactPlugin } from '@stackflow/react';
import type { Activity } from '@stackflow/core';
import { useSpatialPush } from '@/hooks/useSpatialPush';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import {
  ENABLE_SCRIM_BLUR,
  SCRIM_COLOR,
} from '@/utils/spatialTransition';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type RenderedActivity = Activity & {
  key: string;
  render: (overrides?: Partial<Activity>) => ReactNode;
};

interface StackRendererProps {
  stack: {
    render: (overrides?: Record<string, unknown>) => {
      activities: RenderedActivity[];
    };
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// DesktopStackRenderer — No spatial animation; just show the top activity
// ─────────────────────────────────────────────────────────────────────────────

function DesktopStackRenderer({ stack }: StackRendererProps) {
  const activities = stack.render().activities;
  const visible = activities.filter((a) => a.transitionState !== 'exit-done');
  const top = visible[visible.length - 1];

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: '#F1F2F5' }}>
      {visible.map((activity) => (
        <div
          key={activity.key}
          style={{
            position: 'absolute',
            inset: 0,
            visibility: activity.key === top?.key ? 'visible' : 'hidden',
            pointerEvents: activity.key === top?.key ? 'auto' : 'none',
          }}
        >
          {activity.render()}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MobileStackRenderer — iOS 26 Spatial Layering with swipe-back gesture
// ─────────────────────────────────────────────────────────────────────────────

function MobileStackRenderer({ stack }: StackRendererProps) {
  const { pop } = useActions();

  const parentRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);

  const activities = stack.render().activities;
  const visible = activities.filter((a) => a.transitionState !== 'exit-done');
  const count = visible.length;
  const hasChild = count > 1;
  const topActivity = hasChild ? visible[count - 1] : null;
  const topTransitionState = topActivity?.transitionState;

  // Derive isActive: true when child is pushed and not yet exiting
  const isActive = hasChild && topTransitionState !== 'exit-active';

  useSpatialPush({
    parentRef,
    childRef: topRef,
    scrimRef,
    isActive,
    // pushKey: activity identity — detects new pushes & replaces even when
    // isActive stays true (e.g. replace('ChatActivity', ...) from NewConversation)
    pushKey: topActivity?.key,
    onPop: () => pop({ animate: false }),
    stopEdgePropagation: true,
  });

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: '#F1F2F5',
        touchAction: 'pan-y',
      }}
    >
      {visible.map((activity, i) => {
        const isTop = hasChild && i === count - 1;
        const isParent = hasChild && i === count - 2;
        const isHidden = i < count - 2;

        if (isHidden) {
          return (
            <div
              key={activity.key}
              style={{
                position: 'absolute',
                inset: 0,
                visibility: 'hidden',
                pointerEvents: 'none',
              }}
            >
              {activity.render()}
            </div>
          );
        }

        if (isTop) {
          return (
            <div
              key={activity.key}
              ref={topRef}
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 60,
                background: '#F1F2F5',
                visibility: 'hidden',
                overflow: 'hidden',
                touchAction: 'pan-y',
              }}
            >
              {activity.render()}
            </div>
          );
        }

        // Parent or single activity
        return (
          <div
            key={activity.key}
            ref={isParent ? parentRef : undefined}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: i,
              pointerEvents: hasChild ? 'none' : 'auto',
            }}
          >
            {activity.render()}
          </div>
        );
      })}

      {/* Scrim: always rendered, between parent and top — doesn't move with parent */}
      <div
        ref={scrimRef}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 55,
          backgroundColor: SCRIM_COLOR,
          backdropFilter: ENABLE_SCRIM_BLUR ? 'blur(4px)' : undefined,
          WebkitBackdropFilter: ENABLE_SCRIM_BLUR ? 'blur(4px)' : undefined,
          pointerEvents: 'none',
          visibility: 'hidden',
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// StackRendererInner — Picks desktop (instant) or mobile (spatial) renderer
// ─────────────────────────────────────────────────────────────────────────────

function StackRendererInner({ stack }: StackRendererProps) {
  const isMobile = useMediaQuery(MD_BREAKPOINT);

  if (!isMobile) return <DesktopStackRenderer stack={stack} />;
  return <MobileStackRenderer stack={stack} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Plugin export
// ─────────────────────────────────────────────────────────────────────────────

export function stackTransitionPlugin(): StackflowReactPlugin {
  return () => ({
    key: 'stack-transition',
    render({ stack }) {
      return <StackRendererInner stack={stack} />;
    },
  });
}
